import React from 'react';
import {
  Award,
  BookOpen,
  Clock,
  Headphones,
  Laptop,
  Lightbulb,
  Rocket,
  Sparkles,
  Target,
  Trophy,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem
} from '../ui/AnimatedSection';
import { useTenant } from '../../context/TenantContext';
import { inlineEm } from './inlineEm';

// Whitelist of icon names the marketing editor can pick from. Keeps
// tenant-supplied strings from resolving to arbitrary imports.
const ICONS: Record<string, LucideIcon> = {
  book: BookOpen,
  clock: Clock,
  laptop: Laptop,
  award: Award,
  headphones: Headphones,
  users: Users,
  lightbulb: Lightbulb,
  rocket: Rocket,
  sparkles: Sparkles,
  target: Target,
  trophy: Trophy,
};

const DEFAULT_HEADING = 'How we *work*';
const DEFAULT_SUBHEADING =
  'A few of the things that make these programmes worth the time.';

// Returns null when no tenant items — generic "Expert-Led Content" defaults
// were the same fabrication problem as fake hero stats.
export const ValueProps: React.FC = () => {
  const { tenant } = useTenant();
  const marketing = tenant?.branding.marketing;
  const custom = (marketing?.valueProps ?? []).filter(v => v.title?.trim() && v.body?.trim());

  if (custom.length === 0) return null;

  const features = custom.map(v => ({
    icon: ICONS[v.icon?.toLowerCase() ?? ''] ?? Sparkles,
    title: v.title ?? '',
    description: v.body ?? '',
  }));

  const heading = marketing?.copy?.valuePropsHeading?.trim() || DEFAULT_HEADING;
  const subheading = marketing?.copy?.valuePropsSubhead?.trim() || DEFAULT_SUBHEADING;

  return (
    <section id="value-props" className="py-20 md:py-28 bg-gray-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-12 mb-12 md:mb-16 items-baseline">
          <div className="text-xs font-mono uppercase tracking-[0.14em] text-gray-500 border-t-2 border-gray-900 pt-3">
            <span className="block text-primary-600 font-medium mb-1.5">§ Approach</span>
            How we work
          </div>
          <div>
            <h2 className="font-serif text-3xl md:text-5xl text-gray-900 leading-[1.05] tracking-tight mb-4 max-w-[20ch] text-balance">
              {inlineEm(heading)}
            </h2>
            <p className="text-lg text-gray-500 max-w-[52ch] leading-relaxed">{subheading}</p>
          </div>
        </AnimatedSection>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => (
            <StaggerItem key={i}>
              <div className="bg-white p-7 border border-gray-200 transition-colors hover:border-gray-900 h-full">
                <div className="w-10 h-10 mb-5 flex items-center justify-center bg-primary-50 text-primary-700 rounded">
                  <feature.icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-serif text-xl font-medium text-gray-900 mb-2 tracking-tight leading-tight">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-[15px]">
                  {feature.description}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};
